/**
 * Step generation hook — v4 (worker-based).
 *
 * Enqueues step generation as a background job, polls for progress,
 * and invalidates the lesson cache on completion. Resumes progress
 * display if the user navigates away and back.
 *
 * Terminal state (completed/failed) is persisted until the job is
 * acknowledged (dismissed). Acknowledgement from either the sidebar or
 * the inline banner clears state via TanStack Query cache invalidation.
 */
import { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { STEP_TYPES, QUESTION_STEP_TYPES } from '@formency/shared';
import type { BackgroundJob } from '@formency/shared';
import { lessonKeys } from './useLessons';
import { useJob, useMyJobs, jobKeys, useAcknowledgeJob } from './useJobs';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface GenerationProgress {
  phase: string;
  percent: number;
  message?: string;
  error?: string;
}

interface GenerateParams {
  mode: 'manual' | 'extend' | 'fromContent';
  category: 'learn' | 'revise';
  prompt: string;
  stepTypes: string[];
  stepCount: number;
  fileContext?: string | null;
  language?: string;
  imageMode?: 'off' | 'on' | 'force';
}

interface UseStepGenerationOptions {
  lessonId: string;
  courseVersionId?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractProgress(job: BackgroundJob | undefined): GenerationProgress | null {
  if (!job?.progress || typeof job.progress !== 'object') return null;
  const p = job.progress as Record<string, unknown>;
  if (typeof p.phase !== 'string' || typeof p.percent !== 'number') return null;
  return {
    phase: p.phase,
    percent: p.percent,
    ...(typeof p.message === 'string' && { message: p.message }),
    ...(typeof p.error === 'string' && { error: p.error }),
  };
}

function isActive(job: BackgroundJob): boolean {
  return job.status === 'pending' || job.status === 'processing';
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useStepGeneration({ lessonId, courseVersionId }: UseStepGenerationOptions) {
  const qc = useQueryClient();
  const acknowledgeMutation = useAcknowledgeJob();
  const [trackedJobId, setTrackedJobId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Resume: find active or unacknowledged terminal job for this lesson ────
  // Stays enabled while no job is tracked so stale-then-fresh cache updates
  // are picked up (e.g. navigating back to a lesson mid-generation).
  const { data: myJobs } = useMyJobs({ type: 'step_generation' }, { enabled: !trackedJobId });

  useEffect(() => {
    if (trackedJobId || !myJobs) return;

    const active = myJobs.find((j) => j.entityId === lessonId && isActive(j));
    if (active) {
      // Synchronising local state with server job state — intentional

      setTrackedJobId(active.id);
      setIsGenerating(true);
      return;
    }

    // Also check for unacknowledged terminal jobs for this lesson (refresh recovery)
    const terminal = myJobs.find(
      (j) =>
        j.entityId === lessonId && ['completed', 'failed'].includes(j.status) && !j.acknowledgedAt
    );
    if (terminal) {
      setTrackedJobId(terminal.id);
      if (terminal.status === 'failed') {
        setError(terminal.errorMessage ?? 'Step generation failed');
      }
    }
  }, [myJobs, lessonId, trackedJobId]);

  // ── Poll tracked job ──────────────────────────────────────────────────────
  const { data: jobData } = useJob(trackedJobId, {
    refetchInterval: trackedJobId
      ? isGenerating
        ? 2000 // Fast poll while generating
        : 10000 // Slow poll while showing terminal state (detect sidebar acknowledge)
      : false,
    enabled: !!trackedJobId,
  });

  const progress = extractProgress(jobData);

  // React to job status changes
  useEffect(() => {
    if (!jobData || !trackedJobId) return;

    // Job was acknowledged (from sidebar or inline) — clear everything
    if (jobData.acknowledgedAt) {
      // Synchronising local state with polled server status — intentional

      setTrackedJobId(null);
      setIsGenerating(false);
      setError(null);
      return;
    }

    if (jobData.status === 'completed' && isGenerating) {
      setIsGenerating(false);
      qc.invalidateQueries({
        queryKey: [...lessonKeys.withSteps(lessonId), { courseVersionId }],
      });
      void qc.invalidateQueries({ queryKey: jobKeys.all });
    } else if (jobData.status === 'failed' && isGenerating) {
      setIsGenerating(false);
      setError(jobData.errorMessage ?? 'Step generation failed');
      void qc.invalidateQueries({ queryKey: jobKeys.all });
    }
  }, [jobData, trackedJobId, isGenerating, qc, lessonId, courseVersionId]);

  // ── Dismiss terminal state ────────────────────────────────────────────────
  const dismissGeneration = useCallback(() => {
    if (!trackedJobId) return;
    acknowledgeMutation.mutate(trackedJobId);
    // Optimistic clear — the mutation's onSuccess will invalidate jobKeys.all
    setTrackedJobId(null);
    setError(null);
  }, [trackedJobId, acknowledgeMutation]);

  // ── Generate ──────────────────────────────────────────────────────────────
  const generate = useCallback(
    async (params: GenerateParams) => {
      setIsGenerating(true);
      setError(null);
      setTrackedJobId(null);

      try {
        const job = await api.post<BackgroundJob>('/steps/generate', {
          lessonId,
          courseVersionId,
          ...params,
        });
        setTrackedJobId(job.id);
        void qc.invalidateQueries({ queryKey: jobKeys.all });
      } catch (err: unknown) {
        setIsGenerating(false);
        const message = err instanceof Error ? err.message : 'Failed to start step generation';
        setError(message);
      }
    },
    [lessonId, courseVersionId, qc]
  );

  const extendLesson = useCallback(() => {
    void generate({
      mode: 'extend',
      category: 'learn',
      prompt: '',
      stepTypes: [...STEP_TYPES],
      stepCount: 10,
    });
  }, [generate]);

  const autoGenerateRevise = useCallback(() => {
    void generate({
      mode: 'fromContent',
      category: 'revise',
      prompt: '',
      stepTypes: [...QUESTION_STEP_TYPES],
      stepCount: 40,
    });
  }, [generate]);

  // ── Derived terminal job ──────────────────────────────────────────────────
  const terminalJob =
    jobData && !isGenerating && !jobData.acknowledgedAt && trackedJobId ? jobData : null;

  return {
    generate,
    extendLesson,
    autoGenerateRevise,
    isGenerating,
    error,
    progress,
    terminalJob,
    dismissGeneration,
  };
}
